// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { MemberInteractionConsumer, type InteractionType } from '@ta/social.blocks';
import { OverflowUIListItem } from '@ta/social.overflow-ui';
import { ReportMemberModal, ReportModalOpener } from '@ta/public.iap-reporting';

/** A wrapper component for an overflow menu to display options for a particular profile
 * Currently only supports blocking/unblocking and reporting.
 * */

type ProfileOverflowItemType = 'BLOCK' | 'REPORT';
type OverflowItemOverride = { [ProfileOverflowItemType]: boolean };

export type DataProps = {|
  /** The user id of the current profile */
  profileUserId: string,
  /** The display name of the current profile */
  displayName: string,
  /** Whether you are blocking the current user */
  isBlocking: boolean,
  /** Force set an overflow item type, overriding the default business logic for display.
   * This will NOT override feature checks, and overflow items that doesn't have the necessary data will still not display */
  overrideItems?: OverflowItemOverride,
|};

type ExternalProps = {|
  ...DataProps,
  children: (overflowList: React.Element<typeof OverflowUIListItem>[]) => React.Node,
|};

type ConnectedProps = {|
  ...ExternalProps,
  isMe: boolean,
|};

type Props = {|
  ...ConnectedProps,
  openMemberInteractionModal: (type: InteractionType) => mixed,
  openReportModal: () => void,
|};

const ProfileOverflowSelector = ({
  displayName,
  isMe,
  isBlocking,
  openMemberInteractionModal,
  openReportModal,
  overrideItems = {},
  children,
}: Props) => {
  const overflowItems = [];
  // Extract globals separately so the modals don't live in the overflow menu itself
  const globals = [];

  // --- Blocks --
  // Default logic:
  // 1) Can only block  others
  if (
    featureIsEnabled('social_block_enabled') &&
    overrideItems.BLOCK !== false &&
    (overrideItems.BLOCK || !isMe)
  ) {
    // User is currently unblocked and we want to allow current user to block him
    if (!isBlocking) {
      overflowItems.push(
        <OverflowUIListItem
          key="block"
          onClick={() => {
            openMemberInteractionModal('BLOCK');
          }}
        >
          {/** Block {displayName} */}
          {localize('social_block_username_overflow', { displayName })}
        </OverflowUIListItem>,
      );
    }
    // User is currently blocked and we want to allow current user to unblock him
    else {
      overflowItems.push(
        <OverflowUIListItem
          key="unblock"
          onClick={() => {
            openMemberInteractionModal('UNBLOCK');
          }}
        >
          {/** Unblock {displayName} */}
          {localize('social_unblock_username_overflow', { displayName })}
        </OverflowUIListItem>,
      );
    }

    if (
      featureIsEnabled('social_report_profile_iap_enabled') &&
      overrideItems.REPORT !== false &&
      (overrideItems.REPORT || !isMe)
    ) {
      overflowItems.push(
        <OverflowUIListItem key="report" onClick={openReportModal}>
          {/** Report Profile */}
          {localize('social_report_profile')}
        </OverflowUIListItem>,
      );
    }
  }

  return overflowItems.length > 0 ? (
    <React.Fragment>
      {globals}
      {children(overflowItems)}
    </React.Fragment>
  ) : null;
};

const WrappedProfileOverflowSelector = (props: ConnectedProps) => {
  const reportModalName = `reportProfile${props.profileUserId}`;

  return (
    <MemberInteractionConsumer>
      {openModal => (
        <ReportModalOpener name={reportModalName}>
          {openReportModal => (
            <React.Fragment>
              <ReportMemberModal contentId={props.profileUserId} modalName={reportModalName} />
              <ProfileOverflowSelector
                {...props}
                openMemberInteractionModal={openModal}
                openReportModal={openReportModal}
              />
            </React.Fragment>
          )}
        </ReportModalOpener>
      )}
    </MemberInteractionConsumer>
  );
};

export default connect<ConnectedProps, ExternalProps, _, _, _, _>(
  (state, ownProps: ExternalProps) => ({
    isMe: ownProps.profileUserId === state.auth.loggedInUserId,
  }),
)(WrappedProfileOverflowSelector);
